import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import HabboImage from './HabboImage';
import { fetchProfileColor, getAvatarBgStyle } from '../services/profileColorService';

function IconThumbUp({ className }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3H14z"/>
            <path strokeLinecap="round" strokeLinejoin="round" d="M7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"/>
        </svg>
    );
}

function IconThumbDown({ className }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3H10z"/>
            <path strokeLinecap="round" strokeLinejoin="round" d="M17 2h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17"/>
        </svg>
    );
}

function IconClock({ className }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
            <circle cx="12" cy="12" r="10"/>
            <polyline points="12 6 12 12 16 14"/>
        </svg>
    );
}

function IconBold({ className }) {
    return <svg className={className} viewBox="0 0 24 24" fill="currentColor"><path d="M6 4h8a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z"/><path d="M6 12h9a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z"/></svg>;
}

function IconItalic({ className }) {
    return <svg className={className} viewBox="0 0 24 24" fill="currentColor"><line x1="19" y1="4" x2="10" y2="4" stroke="currentColor" strokeWidth="2"/><line x1="14" y1="20" x2="5" y2="20" stroke="currentColor" strokeWidth="2"/><line x1="15" y1="4" x2="9" y2="20" stroke="currentColor" strokeWidth="2"/></svg>;
}

function IconUnderline({ className }) {
    return <svg className={className} viewBox="0 0 24 24" fill="currentColor"><path d="M6 3v7a6 6 0 0 0 6 6 6 6 0 0 0 6-6V3" fill="none" stroke="currentColor" strokeWidth="2"/><line x1="4" y1="21" x2="20" y2="21" stroke="currentColor" strokeWidth="2"/></svg>;
}

function IconTicket({ className }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 0 0-2 2v3a2 2 0 0 1 0 4v3a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-3a2 2 0 0 1 0-4V7a2 2 0 0 0-2-2H5z"/>
        </svg>
    );
}

function VotePill({ id, up, down, selected, onVote, isLoading }) {
    return (
        <div className="flex items-center gap-1 shrink-0">
            <button
                onClick={() => onVote(id, selected === 'like' ? null : 'like')}
                disabled={isLoading}
                className={`flex items-center gap-1 px-2 py-1 rounded text-xs transition-colors ${
                    selected === 'like' ? 'bg-green-500 text-white' : 'bg-gray-300 text-gray-700 hover:bg-green-200'
                }`}
            >
                <IconThumbUp className="w-3 h-3" />
                <span>{up}</span>
            </button>
            <button
                onClick={() => onVote(id, selected === 'dislike' ? null : 'dislike')}
                disabled={isLoading}
                className={`flex items-center gap-1 px-2 py-1 rounded text-xs transition-colors ${
                    selected === 'dislike' ? 'bg-red-500 text-white' : 'bg-gray-300 text-gray-700 hover:bg-red-200'
                }`}
            >
                <IconThumbDown className="w-3 h-3" />
                <span>{down}</span>
            </button>
        </div>
    );
}

function CommentAvatar({ name }) {
    const [avatarBg, setAvatarBg] = useState('#5A2929');

    useEffect(() => {
        if (!name) return;
        let cancelled = false;
        fetchProfileColor(name).then((color) => {
            if (!cancelled) setAvatarBg(color);
        });
        return () => { cancelled = true; };
    }, [name]);

    return (
        <span
            className="w-[42px] h-[42px] overflow-hidden inline-block relative rounded-full border border-black/10 flex-shrink-0 transition-colors duration-300"
            style={getAvatarBgStyle(avatarBg)}
        >
            <HabboImage
                habbo={name}
                hotel="COM"
                size="a"
                direction={2}
                head_direction={3}
                className="absolute top-[-10px] left-[5px] block max-w-none"
            />
        </span>
    );
}

function CommentCard({ c, onVote, selectedVote, isLoading, showVotes }) {
    const timeAgo = (ts) => {
        const diff = Date.now() - new Date(ts).getTime();
        const seconds = Math.floor(diff / 1000);
        const minutes = Math.floor(seconds / 60);
        if (minutes < 60) return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
        const hours = Math.floor(minutes / 60);
        if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
        const days = Math.floor(hours / 24);
        return `${days} day${days > 1 ? 's' : ''} ago`;
    };

    return (
        <div className="bg-gray-200 rounded p-3">
            <div className="flex items-start gap-3">
                <Link to={`/members/profile/${encodeURIComponent(c.user)}`}>
                    <CommentAvatar name={c.user} />
                </Link>
                <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-3 mb-1">
                        <div className="min-w-0">
                            <Link to={`/members/profile/${encodeURIComponent(c.user)}`} className="text-sm font-bold uppercase text-gray-900 hover:underline">{c.user}</Link>
                            <div className="text-xs text-gray-500 uppercase flex items-center gap-1 mt-[2px]">
                                <IconClock className="w-4 h-4" />
                                <span>{timeAgo(c.createdAt)}</span>
                            </div>
                        </div>
                        {showVotes ? (
                            <VotePill
                                id={c.id}
                                up={c.up}
                                down={c.down}
                                selected={selectedVote}
                                onVote={onVote}
                                isLoading={isLoading}
                            />
                        ) : null}
                    </div>
                    <div className="text-sm text-gray-800 leading-relaxed" dangerouslySetInnerHTML={{ __html: c.html }} />
                </div>
            </div>
        </div>
    );
}

function ToolbarButton({ onClick, children, title }) {
    return (
        <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={onClick}
            title={title}
            className="w-[34px] h-[34px] inline-flex items-center justify-center bg-gray-200 hover:bg-gray-300 transition-colors rounded"
        >
            {children}
        </button>
    );
}

function CommentGuidelinesModal({ onCancel, onConfirm, submitting }) {
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="w-full max-w-md border border-black/30 bg-offwhite rounded shadow-lg overflow-hidden">
                <div className="px-4 py-3 border-b border-black/20">
                    <div className="font-bold text-coal text-sm uppercase tracking-wide">Before you post...</div>
                </div>
                <div className="px-4 py-3 space-y-2">
                    <p className="text-sm text-coal">
                        Your comment is subject to approval by the Frankly Team. This is done anonymously and in an unbiased way - Frankly staff cannot see your username until after your post is approved.
                    </p>
                    <p className="text-xs text-coal/50 border-t border-black/20 pt-2 mt-2">
                        Our team will approve your comment if it fits the following criteria:
                    </p>
                    <ul className="list-disc list-inside text-xs text-coal/50">
                        <li>Your comment is clear, to the point, and related to the topic.</li>
                        <li>It's not just spam or fishing for framp cards.</li>
                        <li>It's not abusive, or targeted at an individual or the Habbo staff.</li>
                        <li>It does not contain unnecessary offensive language or terminology.</li>
                    </ul>
                    <p className="text-xs text-coal/50 italic border-t border-black/20 pt-2 mt-2">
                        Note: At Frankly we realise this is an 18+ game and swearing is acceptable, unless it's being used in a way which may cause offense, or is deemed inappropriate.
                    </p>
                </div>
                <div className="px-4 py-3 border-t border-black/20 flex justify-end gap-2">
                    <button
                        type="button"
                        onClick={onCancel}
                        disabled={submitting}
                        className="font-['Jersey_10'] uppercase text-[14px] tracking-[1px] px-4 py-2 bg-gray-300 text-gray-800 hover:bg-gray-400 transition-colors rounded disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={onConfirm}
                        disabled={submitting}
                        className="font-['Jersey_10'] uppercase text-[14px] tracking-[1px] px-4 py-2 bg-maroon text-white hover:bg-red-900 transition-colors rounded disabled:opacity-50"
                    >
                        {submitting ? 'Posting...' : 'Post Comment'}
                    </button>
                </div>
            </div>
        </div>
    );
}

function GiveawayBanner({ availableEntries, hasAlreadyCommented, isLoggedIn }) {
    if (hasAlreadyCommented) {
        return (
            <div className="flex items-start gap-3 bg-green-50 border border-green-300 rounded p-3">
                <div>
                    <div className="font-['Jersey_10'] uppercase text-[14px] tracking-[1px] text-green-800">
                        You're entered!
                    </div>
                    <p className="text-xs text-green-700 mt-0.5">
                        You've already submitted your entry for this giveaway. Good luck!
                    </p>
                </div>
            </div>
        );
    }

    if (!isLoggedIn) {
        return (
            <div className="flex items-start gap-3 bg-amber-50 border border-amber-300 rounded p-3">
                <div>
                    <div className="font-['Jersey_10'] uppercase text-[14px] tracking-[1px] text-amber-800">
                        Giveaway Entry Required
                    </div>
                    <p className="text-xs text-amber-700 mt-0.5">
                        You must be logged in and have a giveaway entry to comment on this article. Log in to check your entries.
                    </p>
                </div>
            </div>
        );
    }

    if (availableEntries === 0) {
        return (
            <div className="flex items-start gap-3 bg-red-50 border border-red-300 rounded p-3">
                <div>
                    <div className="font-['Jersey_10'] uppercase text-[14px] tracking-[1px] text-red-800">
                        No Giveaway Entries
                    </div>
                    <p className="text-xs text-red-700 mt-0.5">
                        Commenting on this article costs 1 giveaway entry, but you don't have any. Visit the Framp Shack to get entries using your framp cards.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="flex items-start gap-3 bg-amber-50 border border-amber-300 rounded p-3">
            <div>
                <div className="font-['Jersey_10'] uppercase text-[14px] tracking-[1px] text-amber-800">
                    Giveaway Entry Required
                </div>
                <p className="text-xs text-amber-700 mt-0.5">
                    Posting a comment on this article will use <strong>1 giveaway entry</strong>. You currently have{' '}
                    <strong>{availableEntries} {availableEntries === 1 ? 'entry' : 'entries'}</strong>. One entry per person!
                </p>
            </div>
        </div>
    );
}

export default function ArticleComments({ articleId, userData }) {
    const [showAll, setShowAll] = useState(false);
    const [draft, setDraft] = useState('');
    const [comments, setComments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [votes, setVotes] = useState({});
    const [votingId, setVotingId] = useState(null);
    const [giveawayInfo, setGiveawayInfo] = useState(null);
    const [showGuidelinesModal, setShowGuidelinesModal] = useState(false);

    useEffect(() => {
        loadComments();
    }, [articleId]);

    const loadComments = async () => {
        try {
            setLoading(true);
            const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api/';
            const response = await fetch(`${apiUrl}comments/articles/${articleId}`, {
                credentials: 'include'
            });

            const data = await response.json();

            if (data.success) {
                setComments(data.comments);
                setGiveawayInfo(data.giveawayInfo || null);
                const votesMap = {};
                data.comments.forEach(c => {
                    if (c.userVote) votesMap[c.id] = c.userVote;
                });
                setVotes(votesMap);
            }
        } catch (error) {
            console.error('Error loading comments:', error);
            toast.error('Failed to load comments');
        } finally {
            setLoading(false);
        }
    };

    const onVote = async (id, next) => {
        if (!userData) {
            toast.error('You must be logged in to vote');
            return;
        }

        try {
            setVotingId(id);
            const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api/';
            const response = await fetch(`${apiUrl}comments/${id}/vote`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ voteType: next })
            });

            const data = await response.json();

            if (data.success) {
                setVotes(prev => ({ ...prev, [id]: next }));
                setComments(prev => prev.map(c =>
                    c.id === id
                        ? { ...c, up: data.likeCount, down: data.dislikeCount }
                        : c
                ));
            }
        } catch (error) {
            console.error('Error voting:', error);
        } finally {
            setVotingId(null);
        }
    };

    const exec = (cmd) => {
        try { document.execCommand(cmd, false, null); } catch (_) {}
    };

    const sanitizeComment = (html) => {
        const allowed = ['b', 'strong', 'i', 'em', 'u'];
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const clean = (node) => {
            if (node.nodeType === Node.TEXT_NODE) return node.cloneNode();
            if (node.nodeType === Node.ELEMENT_NODE) {
                const tag = node.tagName.toLowerCase();
                const children = Array.from(node.childNodes).map(clean).filter(Boolean);
                if (allowed.includes(tag)) {
                    const el = document.createElement(tag);
                    children.forEach(c => el.appendChild(c));
                    return el;
                } else {
                    const frag = document.createDocumentFragment();
                    children.forEach(c => frag.appendChild(c));
                    return frag;
                }
            }
            return null;
        };
        const container = document.createElement('div');
        doc.body.childNodes.forEach(node => {
            const cleaned = clean(node);
            if (cleaned) container.appendChild(cleaned);
        });
        return container.innerHTML;
    };

    const onSubmit = async (e) => {
        e.preventDefault();

        if (!userData) {
            toast.error('You must be logged in to comment');
            return;
        }

        if (!draft.trim()) {
            toast.error('Comment cannot be empty');
            return;
        }

        if (giveawayInfo?.isGiveawayArticle) {
            if (giveawayInfo.hasAlreadyCommented) {
                toast.error('You have already entered this giveaway');
                return;
            }
            if (giveawayInfo.availableEntries === 0) {
                toast.error('You need a giveaway entry to comment here');
                return;
            }
            await doSubmit();
            return;
        }

        setShowGuidelinesModal(true);
    };

    const doSubmit = async () => {
        setShowGuidelinesModal(false);
        try {
            setSubmitting(true);
            const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api/';
            const response = await fetch(`${apiUrl}comments/articles/${articleId}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ commentText: sanitizeComment(draft) })
            });

            const data = await response.json();

            if (data.success) {
                toast.success(data.message);
                setDraft('');
                const editor = document.querySelector('[contentEditable]');
                if (editor) editor.innerHTML = '';
                loadComments();
            } else {
                toast.error(data.message || 'Failed to post comment');
            }
        } catch (error) {
            console.error('Error posting comment:', error);
            toast.error('Failed to post comment');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return <div className="text-center text-gray-700">Loading comments...</div>;
    }

    // Only surface a comment in each panel if it has actual votes of that type
    const withUpvotes = comments.filter(c => c.up > 0);
    const withDownvotes = comments.filter(c => c.down > 0);
    const topUp = withUpvotes.length > 0
        ? [...withUpvotes].sort((a, b) => b.up - a.up)[0]
        : null;
    const topDown = withDownvotes.length > 0
        ? [...withDownvotes].sort((a, b) => b.down - a.down)[0]
        : null;

    const recent = comments.slice(0, 3);

    const isGiveaway = giveawayInfo?.isGiveawayArticle;
    const formLocked = isGiveaway && (
        !userData ||
        giveawayInfo.hasAlreadyCommented ||
        giveawayInfo.availableEntries === 0
    );

    return (
        <div className="space-y-4">
            {showGuidelinesModal && (
                <CommentGuidelinesModal
                    onCancel={() => setShowGuidelinesModal(false)}
                    onConfirm={doSubmit}
                    submitting={submitting}
                />
            )}

            {isGiveaway && (
                <GiveawayBanner
                    availableEntries={giveawayInfo.availableEntries}
                    hasAlreadyCommented={giveawayInfo.hasAlreadyCommented}
                    isLoggedIn={!!userData}
                />
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="bg-gray-200 rounded p-3">
                    <div className="flex items-center justify-between mb-2">
                        <div className="text-xs uppercase tracking-wide font-bold text-gray-700 flex items-center gap-2">
                            <IconThumbUp className="w-4 h-4" />
                            Top Upvoted Comment
                        </div>
                        {topUp ? (
                            <VotePill
                                id={topUp.id}
                                up={topUp.up}
                                down={topUp.down}
                                selected={votes[topUp.id] || null}
                                onVote={onVote}
                                isLoading={votingId === topUp.id}
                            />
                        ) : null}
                    </div>
                    {topUp ? (
                        <CommentCard
                            c={topUp}
                            onVote={onVote}
                            selectedVote={votes[topUp.id] || null}
                            isLoading={votingId === topUp.id}
                            showVotes={false}
                        />
                    ) : (
                        <p className="text-xs text-gray-500">No upvoted comments so far</p>
                    )}
                </div>

                <div className="bg-gray-200 rounded p-3">
                    <div className="flex items-center justify-between mb-2">
                        <div className="text-xs uppercase tracking-wide font-bold text-gray-700 flex items-center gap-2">
                            <IconThumbDown className="w-4 h-4" />
                            Top Downvoted Comment
                        </div>
                        {topDown ? (
                            <VotePill
                                id={topDown.id}
                                up={topDown.up}
                                down={topDown.down}
                                selected={votes[topDown.id] || null}
                                onVote={onVote}
                                isLoading={votingId === topDown.id}
                            />
                        ) : null}
                    </div>
                    {topDown ? (
                        <CommentCard
                            c={topDown}
                            onVote={onVote}
                            selectedVote={votes[topDown.id] || null}
                            isLoading={votingId === topDown.id}
                            showVotes={false}
                        />
                    ) : (
                        <p className="text-xs text-gray-500">No downvoted comments so far</p>
                    )}
                </div>
            </div>

            <div className="space-y-3">
                {(showAll ? comments : recent).map(c => (
                    <CommentCard
                        key={c.id}
                        c={c}
                        onVote={onVote}
                        selectedVote={votes[c.id] || null}
                        isLoading={votingId === c.id}
                        showVotes={!!userData}
                    />
                ))}

                {comments.length > 3 ? (
                    <button
                        type="button"
                        onClick={() => setShowAll(v => !v)}
                        className="text-xs w-full text-center uppercase tracking-wide font-bold text-maroon hover:opacity-80"
                    >
                        {showAll ? 'Show less' : `Show all ${comments.length} comments`}
                    </button>
                ) : null}
            </div>

            <div className="border border-gray-300 rounded overflow-hidden">
                <div className="bg-gray-100 px-3 py-2 border-b border-gray-300">
                    <div className="flex items-center justify-between">
                        <div className="text-xs uppercase tracking-wide font-bold text-gray-700 flex items-center gap-2">
                            {isGiveaway && <IconTicket className="w-4 h-4 text-amber-600" />}
                            {isGiveaway ? 'Submit your giveaway entry' : 'Leave a comment'}
                        </div>
                        <div className="flex items-center gap-2">
                            <ToolbarButton title="Bold" onClick={() => exec('bold')}>
                                <IconBold className="w-4 h-4" />
                            </ToolbarButton>
                            <ToolbarButton title="Italic" onClick={() => exec('italic')}>
                                <IconItalic className="w-4 h-4" />
                            </ToolbarButton>
                            <ToolbarButton title="Underline" onClick={() => exec('underline')}>
                                <IconUnderline className="w-4 h-4" />
                            </ToolbarButton>
                        </div>
                    </div>
                </div>

                {formLocked ? (
                    <div className="px-3 py-4 bg-white text-center">
                        <p className="text-xs text-gray-500 uppercase tracking-wide">
                            {!userData
                                ? 'Log in to leave a comment'
                                : giveawayInfo.hasAlreadyCommented
                                    ? "You've already entered this giveaway"
                                    : 'You need a giveaway entry to comment here'}
                        </p>
                    </div>
                ) : (
                    <form onSubmit={onSubmit}>
                        <div
                            contentEditable
                            suppressContentEditableWarning
                            onInput={(e) => setDraft(e.currentTarget.innerHTML)}
                            className="w-full min-h-[96px] bg-white border-b border-gray-300 p-3 text-sm text-gray-900 focus:outline-none focus:border-maroon"
                            style={{ whiteSpace: 'pre-wrap' }}
                            placeholder="Type your comment..."
                        />

                        <div className="flex items-center justify-between gap-3 px-3 py-2 bg-gray-50">
                            <div className="text-xs text-gray-500 uppercase tracking-wide">
                                {isGiveaway
                                    ? `This will use 1 of your ${giveawayInfo.availableEntries} giveaway ${giveawayInfo.availableEntries === 1 ? 'entry' : 'entries'}. One entry per person.`
                                    : 'Comments are subject to approval. Any rude, targeted or unreasonable comments will be denied.'}
                            </div>
                            <button
                                type="submit"
                                disabled={submitting}
                                className={`text-white px-5 py-2 text-sm uppercase tracking-wider transition-opacity hover:opacity-90 disabled:opacity-50 ${
                                    isGiveaway ? 'bg-amber-600' : 'bg-maroon'
                                }`}
                                style={{ boxShadow: '0 3px 0 0 rgba(0, 0, 0, 0.3)' }}
                            >
                                {submitting
                                    ? (isGiveaway ? 'Entering...' : 'Posting...')
                                    : (isGiveaway ? 'Enter Giveaway' : 'Post Comment')}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
}